import * as Sentry from "@sentry/browser";
import { getEnvVar } from "./environment";
import { filterSentryEvent } from "./sentryFilters";
import { createSupabaseUrlRedactor } from "./supabase-url-redaction";
import { applyTrpcErrorGrouping } from "./trpc-error-grouping";

const dsn = getEnvVar("VITE_PUBLIC_SENTRY_DSN");
if (dsn) {
  // Signed Supabase storage URLs carry their access token in the query string;
  // keep those tokens out of breadcrumbs, replays and error events.
  const redactor = createSupabaseUrlRedactor(
    getEnvVar("VITE_PUBLIC_SUPABASE_URL"),
  );
  Sentry.init({
    dsn,

    // Alternatively, use `process.env.npm_package_version` for a dynamic release version
    // if your build tool supports it.
    release: getEnvVar("RENDER_GIT_COMMIT"),
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.captureConsoleIntegration({
        levels: ["error"],
      }),
    ],

    beforeBreadcrumb: redactor.redactBreadcrumb,
    beforeSendSpan: redactor.redactSpan,
    beforeSendTransaction: redactor.redactTransaction,
    beforeSend: (event, hint) => {
      const kept = filterSentryEvent(event);
      if (!kept) {
        return null;
      }
      return redactor.redactErrorEvent(
        applyTrpcErrorGrouping(kept, hint.originalException),
      );
    },

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for tracing.
    // We recommend adjusting this value in production
    tracesSampleRate: 1.0,

    // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
    tracePropagationTargets: ["localhost", /^https:\/\/my.saileapp\.com/],

    // Capture Replay for 10% of all sessions,
    // plus for 100% of sessions with an error
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });

  // Replay is about 100 kB and nothing needs it to render, so it loads in its
  // own chunk alongside the app instead of in the entry chunk. Recording starts
  // once that chunk arrives, so the first moment of a page load is not replayed.
  //
  // Replay is best-effort, so a failed chunk load is ignored rather than
  // reported. A stale chunk after a deploy is handled by
  // `installStaleBundleReload`, whose `preventDefault` makes this import resolve
  // to `undefined`; other failures, such as being offline, reject and are
  // dropped. Errors from starting Replay itself are not caught here.
  import("./sentry-replay").then(
    (replay) => replay?.startSentryReplay(redactor),
    () => {},
  );
}
